import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, AlertType } from 'components/Alert/Alert'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { Box, Button, Flex, ColoredDot, ColorVariant, Tabs as PillTab, TabsContainer, TablesGrid, colors } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { Badge } from 'components/Badge/Badge'
import { DisplayCard } from 'components/DisplayCards/DisplayCard'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Cell } from 'components/HonestTable/HonestTable.styled'
import { IDashboardTabProps } from 'types/foodLogistics/foodTabProps.interface'
import { IFoodKitchen } from 'types/foodLogistics/foodKitchen.interface'
import { FOOD_STATUS_COLORS } from '../foodData'

export const DashboardTab = ({
  windows,
  kitchens,
  kitchenOrdered,
  confirmations,
  exceptions,
  openExceptions,
  goCreateRequest,
}: IDashboardTabProps) => {
  const navigate = useNavigate()
  const [sumWin, setSumWin] = useState<number>(windows.find((w) => w.locked)?.id || windows[0].id)
  const openExc = exceptions.filter((e) => e.status === 'Open')
  const critHigh = openExc.filter((e) => e.sev === 'Critical' || e.sev === 'High').length

  const servedFor = (w: { id: number }) => kitchens.reduce((s, k) => s + (confirmations[`${w.id}-${k.id}`]?.served || 0), 0)
  const allConfirmed = (wId: number) =>
    kitchens.every((k) => (kitchenOrdered[wId]?.[k.id] || 0) === 0 || confirmations[`${wId}-${k.id}`])
  const canFinalize = critHigh === 0 && windows.filter((w) => w.locked).every((w) => allConfirmed(w.id))

  const summaryColumns: IHonestTableColumn<IFoodKitchen>[] = [
    {
      id: 'kitchen',
      header: 'Kitchen',
      sticky: true,
      minWidth: '12rem',
      cell: (k) => (
        <Cell>
          <BodyText weight='600'>{k.name}</BodyText>
        </Cell>
      ),
    },
    {
      id: 'ordered',
      header: 'Ordered',
      cell: (k) => {
        const w = windows.find((x) => x.id === sumWin)
        const ord = kitchenOrdered[sumWin]?.[k.id] || 0
        return w?.locked ? <BodyText>{ord}</BodyText> : <OtherText grey>Locks at cut-off</OtherText>
      },
    },
    {
      id: 'served',
      header: 'Served',
      cell: (k) => {
        const c = confirmations[`${sumWin}-${k.id}`]
        return <BodyText>{c ? c.served : '—'}</BodyText>
      },
    },
    {
      id: 'standbyUsed',
      header: 'Standby Used',
      cell: (k) => {
        const c = confirmations[`${sumWin}-${k.id}`]
        return <BodyText>{c ? c.standbyUsed : '—'}</BodyText>
      },
    },
    {
      id: 'confirmation',
      header: 'Confirmation',
      cell: (k) => {
        const w = windows.find((x) => x.id === sumWin)
        const c = confirmations[`${sumWin}-${k.id}`]
        const ord = kitchenOrdered[sumWin]?.[k.id] || 0
        if (!w?.locked) {
          return (
            <Badge hexColor={FOOD_STATUS_COLORS['Not Started']}>
              <BodyText>Opens after cut-off</BodyText>
            </Badge>
          )
        }
        if (c) {
          return (
            <Badge hexColor={FOOD_STATUS_COLORS.Served}>
              <Flex gap='0.25rem' center>
                <ColoredDot color={FOOD_STATUS_COLORS.Served} />
                <BodyText>Confirmed{c.onBehalf ? ' (on behalf)' : ''}</BodyText>
              </Flex>
            </Badge>
          )
        }
        if (ord > 0) {
          return (
            <Badge hexColor={FOOD_STATUS_COLORS.High}>
              <BodyText>Awaiting</BodyText>
            </Badge>
          )
        }
        return <OtherText grey>—</OtherText>
      },
    },
    {
      id: 'stock',
      header: 'Standby Stock (per diet)',
      cell: (k) => (
        <Flex wrap gap='0.25rem'>
          {k.standby.map((s) => (
            <Badge key={s.tags.join()} hexColor={s.qty <= s.min ? colors.red : colors.orange}>
              <BodyText>
                {s.tags.join('+')} ×{s.qty}{s.qty <= s.min ? ' ⚠' : ''}
              </BodyText>
            </Badge>
          ))}
        </Flex>
      ),
    },
  ]

  return (
    <Flex column gap='1rem' width='100%'>
      <Flex justend>
        <Button orange width='220' onClick={goCreateRequest}>
          Create Meal Request
        </Button>
      </Flex>

      <Alert
        title='Unknown presence — Dinner window: 12 persons'
        description='Active policy: No Meal — arrivals served from standby. Attendance reconciliation runs until Dinner cut-off (15:30).'
        type={AlertType.WARNING}
        colorVariant={ColorVariant.PRIMARY}
      />

      <Flex gap='0.875rem' width='100%'>
        {windows.map((w) => (
          <Box key={w.id} width='100%' hoverOrangeBorder onClick={() => navigate(`/hr/food/window/${w.id}`)}>
            <Flex between center>
              <BodyText weight='700'>{w.name}</BodyText>
              <Badge hexColor={FOOD_STATUS_COLORS[w.status]}>
                <BodyText>{w.status}</BodyText>
              </Badge>
            </Flex>
            <OtherText grey>
              {w.time} · cut-off {w.cutoff}
            </OtherText>
            <Flex gap='0.5rem' mt='12' width='100%'>
              <DisplayCard value={w.planned} label='Planned' borderColor={colors.orange} />
              <DisplayCard value={w.ordered} label='Ordered' borderColor={FOOD_STATUS_COLORS.Ordered} />
              <DisplayCard value={servedFor(w)} label='Served' borderColor={FOOD_STATUS_COLORS.Served} />
            </Flex>
          </Box>
        ))}
      </Flex>

      <Container>
        <ContainerHeader padding>
          <Flex between center width='100%' wrap gap='0.75rem'>
            <TitleText weight='700'>Kitchen Summary</TitleText>
            <TabsContainer>
              {windows.map((w) => (
                <PillTab key={w.id} active={sumWin === w.id} onClick={() => setSumWin(w.id)}>
                  {w.name}
                </PillTab>
              ))}
            </TabsContainer>
          </Flex>
        </ContainerHeader>
        <ContainerContent>
          <HonestTable columns={summaryColumns} data={kitchens} keyField='id' />
        </ContainerContent>
      </Container>

      <TablesGrid>
        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Exceptions</TitleText>
          </ContainerHeader>
          <ContainerContent gap='0.75rem'>
            <Flex gap='0.625rem' >
              {(['Critical', 'High', 'Medium', 'Low'] as const).map((s) => (
                <DisplayCard
                  key={s}
                  value={openExc.filter((e) => e.sev === s).length}
                  label={s}
                  borderColor={FOOD_STATUS_COLORS[s]}
                />
              ))}
            </Flex>
            <Button orangeBorder onClick={openExceptions}>
              Open Exceptions
            </Button>
          </ContainerContent>
        </Container>

        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Operational Day Close</TitleText>
          </ContainerHeader>
          <ContainerContent gap='0.75rem'>
            {!canFinalize ? (
              <Alert
                title='Auto-finalize blocked'
                description={`${!windows
                  .filter((w) => w.locked)
                  .every((w) => kitchens.every((k) => (kitchenOrdered[w.id]?.[k.id] || 0) === 0 || confirmations[`${w.id}-${k.id}`]))
                  ? 'Kitchen confirmation(s) missing. '
                  : ''
                  }${critHigh > 0 ? `${critHigh} open Critical/High exception(s).` : ''}`}
                type={AlertType.WARNING}
                colorVariant={ColorVariant.PRIMARY}
              />
            ) : (
              <Alert
                title='Ready to finalize'
                description='All conditions met — day will auto-finalize at close.'
                type={AlertType.SUCCESS}
                colorVariant={ColorVariant.GREEN}
              />
            )}
            <Button orange disabled={!canFinalize}>
              Finalize Now
            </Button>
          </ContainerContent>
        </Container>
      </TablesGrid>
    </Flex>
  )
}
